import { Injectable, Inject } from '@angular/core';
import {
  OAuthTokenType,
  OidcConfig
} from '../configuration/oidc-config.settings';
import { HttpClient } from '@angular/common/http';
import {
  SecurityConfigInjectionToken,
  SecurityConfig
} from '../../configuration/security.config';
import { catchError, map, Observable, of, take } from 'rxjs';
import { LogLevel, OpenIdConfiguration } from 'angular-auth-oidc-client';

@Injectable({
  providedIn: 'root'
})

/**
 * Provides configuration mapping for the OIDC library
 */
export class OidcConfigService {
  private config: OidcConfig;

  constructor(
    @Inject(SecurityConfigInjectionToken) secConfig: SecurityConfig,
    private httpClient: HttpClient
  ) {
    if (secConfig === undefined || secConfig === null) {
      throw new Error('No configuration provided for the OpenIDConnectModule');
    }
    this.config = secConfig.configuration;
  }

  /**
   * @description OIDC Version 15 upgrade: creating cunstom confguration as per oidc version 15
   *
   * @return {*}  {Observable<Object>}
   * @memberof OidcConfigService
   */
  loadConfiguration(): Observable<Object> {
    // OIDC Version 15 upgrade: keys are accepted in camelCase in oidc and we get the keys from endpoints with '_'.
    // So, we need the conversion function
    const changeConfigKeysToCamelCase = key => {
      return key.replace(/_./g, m => m[1].toUpperCase());
    };
    const config$ = this.httpClient.get(this.config.autoConfigUrl).pipe(
      take(1),
      map((customConfig: any) => {
        // OIDC Version 15 upgrade: get the static config for oidc
        const config = this.getConfig();
        // OIDC Version 15 upgrade: update the confuguration returned by auto config url in oidc compatible format
        const newCustomConfig = Object.keys(customConfig).reduce(
          (acc, k) => ({
            ...acc,
            [changeConfigKeysToCamelCase(k)]: customConfig[k]
          }),
          {}
        );
        // OIDC Version 15 upgrade: update the auth well known end points by combining static config with Http config
        // Http config takes precedence over static config
        config['authWellknownEndpoints'] = {
          ...config['authWellknownEndpoints'],
          ...customConfig,
          ...newCustomConfig
        };
        config['authority'] = customConfig['issuer'];

        this.config = { ...this.config, ...config };
        return config;
      }),
      catchError(error => {
        console.error(
          'Unable to load the wellknown endpoints from "' +
            this.config.autoConfigUrl +
            '" Please ensure that the website is responding, is compliant with the OIDC specification and that cors is enabled.',
          error
        );
        return of([]);
      })
    );

    return config$;
  }

  /**
   * @description OIDC Version 15 upgrade : utility to set the configuration manually
   *
   * @return {*}  {OpenIdConfiguration}
   * @memberof OidcConfigService
   */
  getConfig(): OpenIdConfiguration {
    // You can use this property to test the renew functionality
    // renewTimeBeforeTokenExpiresInSeconds: 3580,
    let settings: OpenIdConfiguration = {
      disablePkce: false,
      responseType: 'code',
      triggerAuthorizationResultEvent: true,
      autoUserInfo: false,
      startCheckSession: false,
      renewUserInfoAfterTokenRenew: true,
    };

    settings = { ...settings, ...this.mapExtendedOptions() };
    return settings;
  }
  /**
   * Maps the user configured settings over to the library configuration
   */
  private mapExtendedOptions() {
    let settings: OpenIdConfiguration = {};
    settings.clientId = this.config.clientId;
    settings.scope = `openid`;
    settings.logLevel = LogLevel.None;
    settings.unauthorizedRoute = this.config.routes.forbiddenRoute;
    settings.forbiddenRoute = this.config.routes.forbiddenRoute;
    if (
      this.config.maxTimeOffsetSeconds &&
      this.config.maxTimeOffsetSeconds > 10
    ) {
      settings.maxIdTokenIatOffsetAllowedInSeconds =
        this.config.maxTimeOffsetSeconds;
    }
    if (this.config.silentRenew && this.config.routes.silentRenewUrl) {
      settings.silentRenewUrl = this.config.routes.silentRenewUrl;
      settings.silentRenew = this.config.silentRenew;
    }
    if (this.config.routes.logoutUrl) {
      settings.postLogoutRedirectUri = this.config.routes.logoutUrl;
      if (!settings.unauthorizedRoute) {
        settings.unauthorizedRoute = this.config.routes.logoutUrl;
        settings.forbiddenRoute = this.config.routes.logoutUrl;
      }
    }
    if (this.config.routes.redirectUrl) {
      settings.redirectUrl = this.config.routes.redirectUrl;
    }
    if (this.config.scopes) {
      settings.scope += ` ${this.config.scopes.join(' ')}`;
    }
    if (this.config.diagnostics) {
      if (this.config.diagnostics.logWarning) {
        settings.logLevel = LogLevel.Warn;
      }
      if (this.config.diagnostics.logDebug) {
        settings.logLevel = LogLevel.Debug;
      }
    }
    let oAuthOptions = {};
    if (this.config && this.config.accessTokenTypeHint) {
      switch (this.config.accessTokenTypeHint) {
        case OAuthTokenType.Local:
        case OAuthTokenType.Stream:
          oAuthOptions = {
            csx_access_token_hint: this.config.accessTokenTypeHint.toString()
          };
          break;
        default:
          break;
      }
    }
    if (oAuthOptions) {
      settings.customParamsAuthRequest = oAuthOptions;
      settings.customParamsRefreshTokenRequest = oAuthOptions;
      settings.customParamsCodeRequest = oAuthOptions;
      settings.customParamsEndSessionRequest = oAuthOptions;
    }
    return settings;
  }

  public get autoLogin(): boolean {
    return this.config.autoLogin ? true : false;
  }
  public get handleError(): boolean {
    return this.config.hasOwnProperty('handleError') ? this.config.handleError : true;
  }
}
